'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';

// ─── GRAPHICS DATA ────────────────────────────────────────────────────────────
// Sourced from Behance gallery: https://www.behance.net/gallery/112291665/Monolyth-(VR)
// Graphics section — actual graphic design works: event posters, illustrations, typography
// Images verified from live Behance page (67fbabbd series)

interface GraphicsItem {
  id: number;
  src: string;
  alt: string;
  title: string;
  description: string;
  medium: string;
  year: string;
  category: 'glyph' | 'environment' | 'mapping' | 'identity' | 'process';
  bridgeLinks: {label: string;href: string;color: string;}[];
}

// Collage / hero image for the Graphics section
const GRAPHICS_COLLAGE_SRC =
'https://mir-s3-cdn-cf.behance.net/project_modules/1400_webp/2ca22d112291665.67fbabc561e05.jpg';

const GRAPHICS: GraphicsItem[] = [
{
  id: 1,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/0abbdf112291665.67fbabbd2c409.jpg',
  alt: 'Monolyth graphics — event poster design with urban typography and community imagery',
  title: 'Community Event Poster I',
  description: 'Event poster design from the Pittsburgh research phase — combining urban photography, bold typography, and community imagery to create visual communication for local cultural events.',
  medium: 'Digital / Graphic Design',
  year: '2015',
  category: 'identity',
  bridgeLinks: [
  { label: 'Keys to Kulli Haal', href: '/keys-to-kulli-haal', color: '#D4A82A' },
  { label: 'SL Glossary', href: '/sl-glossary', color: '#c9a84c' }]

},
{
  id: 2,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/cd579d112291665.67fbabbd2d1c8.jpg',
  alt: 'Monolyth graphics — Chicago All-City Visual Arts Exhibition poster design',
  title: 'Chicago All-City Visual Arts Exhibition',
  description: 'Poster design for the Chicago Public Schools All-City Visual Arts Exhibition — a graphic design work documenting community cultural programming and youth arts education.',
  medium: 'Digital / Poster Design',
  year: '2015',
  category: 'identity',
  bridgeLinks: [
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' },
  { label: 'Open Letter', href: '/open-letter', color: '#c9a84c' }]

},
{
  id: 3,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/cc2b46112291665.67fbabbd3185c.jpg',
  alt: 'Monolyth graphics — Teen Improv Exchange event poster with bold typography',
  title: 'Teen Improv Exchange Poster',
  description: 'Event poster for the Annual Teen Improv Exchange — graphic design work combining bold typography with community arts programming visual identity.',
  medium: 'Digital / Event Design',
  year: '2015',
  category: 'process',
  bridgeLinks: [
  { label: 'Keys to Kulli Haal', href: '/keys-to-kulli-haal', color: '#D4A82A' },
  { label: 'MRG', href: '/muslim-resource-guide', color: '#2d7a47' }]

},
{
  id: 4,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/f3f549112291665.67fbabbd2d8f5.jpg',
  alt: 'Monolyth graphics — Chicago Goes Live talent show poster design',
  title: 'Chicago Goes Live — Talent Show',
  description: 'Poster design for the Chicago Goes Live Talent Show — a graphic work documenting youth performance culture and community arts programming in Chicago.',
  medium: 'Digital / Poster Design',
  year: '2015',
  category: 'identity',
  bridgeLinks: [
  { label: 'Keys to Kulli Haal', href: '/keys-to-kulli-haal', color: '#D4A82A' },
  { label: 'Open Letter', href: '/open-letter', color: '#c9a84c' }]

},
{
  id: 5,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/3e7c73112291665.67fbabbd2bf0f.jpg',
  alt: 'Monolyth graphics — abstract illustration with geometric forms and cultural symbols',
  title: 'Abstract Cultural Illustration',
  description: 'Abstract illustration combining geometric forms with cultural symbols — an exploration of visual language that would evolve into the Monolyth glyph system.',
  medium: 'Digital / Illustration',
  year: '2016',
  category: 'glyph',
  bridgeLinks: [
  { label: 'Ayat in Creation', href: '/ayat-in-creation', color: '#2d8a87' },
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' }]

},
{
  id: 6,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/9fc4a2112291665.67fbabbd2e0e8.jpg',
  alt: 'Monolyth graphics — MC Lyte concert poster with bold typography and urban imagery',
  title: 'MC Lyte Concert Poster',
  description: 'Concert poster design for MC Lyte — graphic design work combining bold typography with hip-hop cultural imagery, documenting the intersection of music and community.',
  medium: 'Digital / Poster Design',
  year: '2015',
  category: 'identity',
  bridgeLinks: [
  { label: 'MRG', href: '/muslim-resource-guide', color: '#2d7a47' },
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' }]

},
{
  id: 7,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/b07b97112291665.67fbabbd2e68c.jpg',
  alt: 'Monolyth graphics — HHW Vocal Ensemble performance poster design',
  title: 'HHW Vocal Ensemble Poster',
  description: 'Performance poster for the HHW Vocal Ensemble — graphic design documenting community arts programming and youth performance culture.',
  medium: 'Digital / Event Design',
  year: '2015',
  category: 'identity',
  bridgeLinks: [
  { label: 'MRG', href: '/muslim-resource-guide', color: '#2d7a47' },
  { label: 'Open Letter', href: '/open-letter', color: '#c9a84c' }]

},
{
  id: 8,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/1400/3723d6112291665.67fbabbd2eae3.jpg',
  alt: 'Monolyth graphics — vertical format poster with layered typography and urban photography',
  title: 'Urban Typography Poster',
  description: 'Vertical format poster combining layered typography with urban photography — an exploration of the visual language that would inform the Monolyth design system.',
  medium: 'Digital / Typography',
  year: '2016',
  category: 'process',
  bridgeLinks: [
  { label: 'Open Letter', href: '/open-letter', color: '#c9a84c' },
  { label: 'Keys to Kulli Haal', href: '/keys-to-kulli-haal', color: '#D4A82A' }]

},
{
  id: 9,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/ed0c9c112291665.67fbabbd2ef3d.jpg',
  alt: 'Monolyth graphics — tall vertical poster design with community documentation',
  title: 'Community Documentation Poster',
  description: 'Tall vertical poster design documenting community cultural programming — the graphic design practice that would evolve into the Monolyth visual documentation methodology.',
  medium: 'Digital / Documentation',
  year: '2016',
  category: 'mapping',
  bridgeLinks: [
  { label: 'SL Glossary', href: '/sl-glossary', color: '#c9a84c' },
  { label: 'Open Letter', href: '/open-letter', color: '#c9a84c' }]

},
{
  id: 10,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/1400/4d6a05112291665.67fbabbd2f41a.jpg',
  alt: 'Monolyth graphics — vertical format community arts poster with bold design',
  title: 'Community Arts Vertical Poster',
  description: 'Vertical format community arts poster — graphic design work from the Chicago research phase documenting youth arts programming and cultural events.',
  medium: 'Digital / Poster Design',
  year: '2016',
  category: 'identity',
  bridgeLinks: [
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' },
  { label: 'Keys to Kulli Haal', href: '/keys-to-kulli-haal', color: '#D4A82A' }]

},
{
  id: 11,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/6b4b2b112291665.67fbabbd2cc69.jpg',
  alt: 'Monolyth graphics — tall vertical illustration with layered cultural imagery',
  title: 'Layered Cultural Illustration',
  description: 'Tall vertical illustration combining layered cultural imagery — an exploration of visual depth and community storytelling that informs the Monolyth layered environment design.',
  medium: 'Digital / Illustration',
  year: '2017',
  category: 'environment',
  bridgeLinks: [
  { label: 'Ayat in Creation', href: '/ayat-in-creation', color: '#2d8a87' },
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' }]

},
{
  id: 12,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/905f0c112291665.67fbabbd313b5.jpg',
  alt: 'Monolyth graphics — horizontal banner design with community event information',
  title: 'Community Event Banner',
  description: 'Horizontal banner design for community events — graphic design work documenting the visual communication systems developed during the Pittsburgh and Chicago research phases.',
  medium: 'Digital / Banner Design',
  year: '2015',
  category: 'mapping',
  bridgeLinks: [
  { label: 'MRG', href: '/muslim-resource-guide', color: '#2d7a47' },
  { label: 'Open Letter', href: '/open-letter', color: '#c9a84c' }]

},
{
  id: 13,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/bfdded112291665.67fbabbd2f896.jpg',
  alt: 'Monolyth graphics — tall vertical poster with abstract urban photography and typography',
  title: 'Abstract Urban Photography Poster',
  description: 'Tall vertical poster combining abstract urban photography with bold typography — a graphic design work exploring the visual language of environmental documentation.',
  medium: 'Digital / Photography + Design',
  year: '2017',
  category: 'process',
  bridgeLinks: [
  { label: 'Open Letter', href: '/open-letter', color: '#c9a84c' },
  { label: 'SL Glossary', href: '/sl-glossary', color: '#c9a84c' }]

},
{
  id: 14,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/ea86ef112291665.67fbabbd30caf.jpg',
  alt: 'Monolyth graphics — very tall vertical poster with layered community documentation',
  title: 'Extended Community Documentation',
  description: 'Extended vertical format poster with layered community documentation — the graphic design methodology that would evolve into the Monolyth multi-layer data visualization system.',
  medium: 'Digital / Documentation',
  year: '2017',
  category: 'mapping',
  bridgeLinks: [
  { label: 'MRG', href: '/muslim-resource-guide', color: '#2d7a47' },
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' }]

},
{
  id: 15,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/max_3840_webp/f2b14f112291665.67fbabbd2fdc7.jpg',
  alt: 'Monolyth graphics — horizontal format graphic with community event design',
  title: 'Horizontal Event Graphic',
  description: 'Horizontal format graphic design for community events — a visual communication work from the research phase documenting the intersection of design and community organizing.',
  medium: 'Digital / Event Design',
  year: '2016',
  category: 'identity',
  bridgeLinks: [
  { label: 'Keys to Kulli Haal', href: '/keys-to-kulli-haal', color: '#D4A82A' },
  { label: 'Open Letter', href: '/open-letter', color: '#c9a84c' },
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' }]

},
{
  id: 16,
  src: 'https://mir-s3-cdn-cf.behance.net/project_modules/1400/4faaa5112291665.67fbabbd304f0.jpg',
  alt: 'Monolyth graphics — final graphic design synthesis with community visual language',
  title: 'Visual Language Synthesis',
  description: 'The final synthesis of the Monolyth graphic design language — community documentation, event design, and visual communication unified into a coherent visual system that would become the foundation of the Haal Project design identity.',
  medium: 'Digital / Synthesis',
  year: '2018',
  category: 'identity',
  bridgeLinks: [
  { label: 'Open Letter', href: '/open-letter', color: '#c9a84c' },
  { label: 'Keys to Kulli Haal', href: '/keys-to-kulli-haal', color: '#D4A82A' },
  { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' }]

}];


const CATEGORY_LABELS: Record<GraphicsItem['category'], string> = {
  glyph: 'Glyph System',
  environment: 'Environment',
  mapping: 'Mapping',
  identity: 'Identity',
  process: 'Process'
};

const CATEGORY_COLORS: Record<GraphicsItem['category'], string> = {
  glyph: '#8a8ab8',
  environment: '#2d8a87',
  mapping: '#2d7a47',
  identity: '#c9a84c',
  process: '#D4A82A'
};

// ─── LIGHTBOX ─────────────────────────────────────────────────────────────────
interface LightboxProps {
  item: GraphicsItem;
  total: number;
  currentIndex: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}

function GraphicsLightbox({ item, total, currentIndex, onClose, onPrev, onNext }: LightboxProps) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose, onPrev, onNext]);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(4,8,12,0.97)',
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}
      onClick={onClose}>
      
      <div
        style={{
          maxWidth: 1100, width: '95vw', maxHeight: '92vh',
          display: 'flex', flexDirection: 'column', gap: 0,
          background: '#0a1018',
          border: '1px solid rgba(138,138,184,0.2)',
          borderRadius: 4,
          overflow: 'hidden'
        }}
        onClick={(e) => e.stopPropagation()}>
        
        {/* Top bar */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 16px',
          borderBottom: '1px solid rgba(138,138,184,0.12)',
          background: '#060d12',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{
              fontFamily: 'monospace', fontSize: 8, letterSpacing: '.18em',
              textTransform: 'uppercase', color: 'rgba(138,138,184,0.5)'
            }}>
              ◈ Graphics · {String(currentIndex + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
            </span>
            <span style={{
              fontFamily: 'monospace', fontSize: 7, letterSpacing: '.1em',
              textTransform: 'uppercase', color: CATEGORY_COLORS[item.category],
              padding: '2px 7px', border: `1px solid ${CATEGORY_COLORS[item.category]}40`,
              borderRadius: 2
            }}>
              {CATEGORY_LABELS[item.category]}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: '1px solid rgba(138,138,184,0.2)',
              color: 'rgba(212,197,169,0.5)', cursor: 'pointer',
              width: 28, height: 28, borderRadius: 3, fontSize: 14,
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
            ✕</button>
        </div>

        {/* Image + info */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
          {/* Image */}
          <div style={{
            flex: '1 1 60%', position: 'relative', background: '#040810',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden'
          }}>
            <button
              onClick={onPrev}
              style={{
                position: 'absolute', left: 12, zIndex: 2,
                background: 'rgba(4,8,12,0.7)', border: '1px solid rgba(138,138,184,0.2)',
                color: 'rgba(212,197,169,0.7)', cursor: 'pointer',
                width: 36, height: 36, borderRadius: 3, fontSize: 16,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
              ‹</button>
            <img
              src={item.src}
              alt={item.alt}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            
            <button
              onClick={onNext}
              style={{
                position: 'absolute', right: 12, zIndex: 2,
                background: 'rgba(4,8,12,0.7)', border: '1px solid rgba(138,138,184,0.2)',
                color: 'rgba(212,197,169,0.7)', cursor: 'pointer',
                width: 36, height: 36, borderRadius: 3, fontSize: 16,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
              ›</button>
          </div>

          {/* Info panel */}
          <div style={{
            flex: '0 0 300px', padding: '24px 20px', overflowY: 'auto',
            borderLeft: '1px solid rgba(138,138,184,0.12)',
            background: '#060d12',
            display: 'flex', flexDirection: 'column', gap: 16
          }}>
            <div>
              <p style={{
                fontFamily: 'monospace', fontSize: 8, letterSpacing: '.15em',
                textTransform: 'uppercase', color: 'rgba(138,138,184,0.4)',
                marginBottom: 6
              }}>
                {item.medium} · {item.year}
              </p>
              <h3 style={{
                fontFamily: "'Amiri', serif", fontSize: 20, color: '#F5F0E8',
                fontWeight: 400, lineHeight: 1.3, marginBottom: 12
              }}>
                {item.title}
              </h3>
              <p style={{
                fontFamily: "'Georgia', serif", fontSize: 13, color: 'rgba(212,197,169,0.65)',
                lineHeight: 1.75
              }}>
                {item.description}
              </p>
            </div>

            {/* Bridge links */}
            <div>
              <p style={{
                fontFamily: 'monospace', fontSize: 7, letterSpacing: '.15em',
                textTransform: 'uppercase', color: 'rgba(138,138,184,0.35)',
                marginBottom: 8
              }}>
                ◈ Bridge Links
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {item.bridgeLinks.map((link) =>
                <Link
                  key={link.href}
                  href={link.href}
                  style={{
                    fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em',
                    textTransform: 'uppercase', color: link.color,
                    textDecoration: 'none',
                    padding: '6px 10px',
                    border: `1px solid ${link.color}30`,
                    borderRadius: 3,
                    background: `${link.color}08`,
                    display: 'flex', alignItems: 'center', gap: 6,
                    transition: 'all 0.15s'
                  }}>
                  
                    <span style={{ opacity: 0.6 }}>→</span> {link.label}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>);

}

// ─── GRAPHICS SECTION ─────────────────────────────────────────────────────────
export default function GraphicsSection() {
  const [activeFilter, setActiveFilter] = useState<GraphicsItem['category'] | 'all'>('all');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const filtered = activeFilter === 'all' ?
  GRAPHICS :
  GRAPHICS.filter((g) => g.category === activeFilter);

  const openLightbox = useCallback((idx: number) => setLightboxIndex(idx), []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const prevImage = useCallback(() => {
    setLightboxIndex((i) => i === null ? null : (i - 1 + filtered.length) % filtered.length);
  }, [filtered.length]);
  const nextImage = useCallback(() => {
    setLightboxIndex((i) => i === null ? null : (i + 1) % filtered.length);
  }, [filtered.length]);

  const categories: Array<GraphicsItem['category'] | 'all'> = ['all', 'glyph', 'environment', 'mapping', 'identity', 'process'];

  return (
    <section id="graphics" style={{ paddingBottom: 80, borderTop: '1px solid rgba(138,138,184,0.12)' }}>
      {/* Lightbox */}
      {lightboxIndex !== null &&
      <GraphicsLightbox
        item={filtered[lightboxIndex]}
        total={filtered.length}
        currentIndex={lightboxIndex}
        onClose={closeLightbox}
        onPrev={prevImage}
        onNext={nextImage} />

      }

      <div style={{ padding: '60px 7.1% 40px' }}>
        {/* Section header */}
        <div style={{ marginBottom: 40 }}>
          <p style={{
            fontFamily: 'monospace', fontSize: 9, letterSpacing: '.18em',
            textTransform: 'uppercase', color: 'rgba(138,138,184,0.5)', marginBottom: 10
          }}>
            ◈ Portfolio · Segment II
          </p>
          <h2 style={{
            fontFamily: "'Amiri', serif", fontSize: 42, color: '#F5F0E8',
            fontWeight: 400, marginBottom: 14, lineHeight: 1.1
          }}>
            Graphics
          </h2>
          <p style={{
            fontFamily: "'Georgia', serif", fontSize: 15, color: 'rgba(212,197,169,0.6)',
            lineHeight: 1.8, maxWidth: 680, marginBottom: 24
          }}>
            Graphic design work from the Monolyth era — glyph systems encoding community data, environmental visualizations, mapping graphics, and the visual identity language that evolved into the Haal Project's design system.
          </p>

          {/* Bridge links row */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 28 }}>
            {[
            { label: 'IRL Lens', href: '/inner-reality-lens', color: '#8a8ab8' },
            { label: 'MRG', href: '/muslim-resource-guide', color: '#2d7a47' },
            { label: 'Open Letter', href: '/open-letter', color: '#c9a84c' }].
            map((link) =>
            <Link
              key={link.href}
              href={link.href}
              style={{
                fontFamily: 'monospace', fontSize: 8, letterSpacing: '.12em',
                textTransform: 'uppercase', color: link.color,
                textDecoration: 'none', padding: '5px 12px',
                border: `1px solid ${link.color}30`,
                borderRadius: 3, background: `${link.color}08`
              }}>
              
                → {link.label}
              </Link>
            )}
          </div>
        </div>

        {/* Main collage image */}
        <div style={{ marginBottom: 32, position: 'relative' }}>
          <img
            src={GRAPHICS_COLLAGE_SRC}
            alt="Monolyth (VR) — main collage image showing the full visual language system across Pittsburgh, Detroit, and Chicago environments"
            style={{
              width: '100%', display: 'block',
              borderRadius: 3,
              border: '1px solid rgba(138,138,184,0.12)'
            }} />
          
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            padding: '20px 20px 16px',
            background: 'linear-gradient(to top, rgba(4,8,12,0.9) 0%, transparent 100%)',
            borderRadius: '0 0 3px 3px'
          }}>
            <p style={{
              fontFamily: 'monospace', fontSize: 8, letterSpacing: '.15em',
              textTransform: 'uppercase', color: 'rgba(138,138,184,0.6)', margin: 0
            }}>
              ◈ Monolyth (VR) — Visual Language System · 2018–2021
            </p>
          </div>
        </div>

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
          {categories.map((cat) =>
          <button
            key={cat}
            onClick={() => setActiveFilter(cat)}
            style={{
              fontFamily: 'monospace', fontSize: 8, letterSpacing: '.12em',
              textTransform: 'uppercase', padding: '5px 12px', borderRadius: 3,
              cursor: 'pointer', transition: 'all 0.15s',
              border: `1px solid ${activeFilter === cat ? 'rgba(138,138,184,0.5)' : 'rgba(138,138,184,0.15)'}`,
              background: activeFilter === cat ? 'rgba(138,138,184,0.1)' : 'transparent',
              color: activeFilter === cat ? '#8a8ab8' : 'rgba(212,197,169,0.4)'
            }}>
            
              {cat === 'all' ? `All (${GRAPHICS.length})` : `${CATEGORY_LABELS[cat as GraphicsItem['category']]} (${GRAPHICS.filter((g) => g.category === cat).length})`}
            </button>
          )}
        </div>

        {/* 16-image grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 3
        }}>
          {filtered.map((item, idx) =>
          <div
            key={item.id}
            onClick={() => openLightbox(idx)}
            style={{
              position: 'relative', cursor: 'pointer',
              aspectRatio: '4/3', overflow: 'hidden',
              background: '#040810',
              border: '1px solid rgba(138,138,184,0.08)'
            }}>
            
              <img
              src={item.src}
              alt={item.alt}
              style={{
                width: '100%', height: '100%', objectFit: 'cover',
                transition: 'transform 0.3s ease',
                display: 'block'
              }}
              onMouseEnter={(e) => {(e.currentTarget as HTMLImageElement).style.transform = 'scale(1.04)';}}
              onMouseLeave={(e) => {(e.currentTarget as HTMLImageElement).style.transform = 'scale(1)';}} />
            
              {/* Hover overlay */}
              <div style={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(to top, rgba(4,8,12,0.85) 0%, transparent 50%)',
              opacity: 0, transition: 'opacity 0.2s',
              display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
              padding: '10px 10px 8px'
            }}
            onMouseEnter={(e) => {(e.currentTarget as HTMLDivElement).style.opacity = '1';}}
            onMouseLeave={(e) => {(e.currentTarget as HTMLDivElement).style.opacity = '0';}}>
              
                <p style={{
                fontFamily: 'monospace', fontSize: 7, letterSpacing: '.1em',
                textTransform: 'uppercase', color: CATEGORY_COLORS[item.category],
                margin: '0 0 3px'
              }}>
                  {CATEGORY_LABELS[item.category]}
                </p>
                <p style={{
                fontFamily: "'Georgia', serif", fontSize: 11, color: '#F5F0E8',
                margin: 0, lineHeight: 1.3
              }}>
                  {item.title}
                </p>
              </div>
              {/* Index badge */}
              <div style={{
              position: 'absolute', top: 6, left: 6,
              fontFamily: 'monospace', fontSize: 7, letterSpacing: '.08em',
              color: 'rgba(138,138,184,0.5)',
              background: 'rgba(4,8,12,0.7)',
              padding: '2px 5px', borderRadius: 2
            }}>
                {String(idx + 1).padStart(2, '0')}
              </div>
            </div>
          )}
        </div>

        {/* Count label */}
        <div style={{ marginTop: 12, textAlign: 'right' }}>
          <span style={{
            fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em',
            color: 'rgba(138,138,184,0.35)'
          }}>
            {filtered.length} graphic{filtered.length !== 1 ? 's' : ''} · click to expand
          </span>
        </div>
      </div>
    </section>);

}